import { useState, useCallback } from 'react';
import { updateTarifarios } from '@shared/helpers/api/mantenedores/tarifarios';

/**
 * Hook para manejar la actualización de tarifarios
 */
export const useTarifariosUpdate = (user) => {
    const [updating, setUpdating] = useState(false);
    const [updateError, setUpdateError] = useState(null);

    /**
     * Actualizar un solo tarifario
     * @param {number} id - ID del tarifario
     * @param {number} precio - Nuevo precio
     * @param {string} modalidad - Nueva modalidad ('Fijo' o 'Variable')
     * @returns {Promise<Object>} Resultado de la operación
     */
    const updateSingleTarifario = useCallback(async (id, precio, modalidad) => {
        if (!user) {
            return {
                success: false,
                error: 'Usuario no autenticado'
            };
        }

        setUpdating(true);
        setUpdateError(null);

        try {
            // Validar datos
            if (!id || precio === undefined || precio === null || !modalidad) {
                throw new Error('Todos los campos son requeridos: id, precio, modalidad');
            }

            // Convertir precio a número
            const precioNumerico = parseFloat(precio);
            if (isNaN(precioNumerico) || precioNumerico < 0) {
                throw new Error('El precio debe ser un número válido mayor o igual a 0');
            }

            // Validar modalidad
            if (!['Fijo', 'Variable'].includes(modalidad)) {
                throw new Error('La modalidad debe ser "Fijo" o "Variable"');
            }

            const tarifarios = [{
                id: parseInt(id),
                precio: precioNumerico,
                modalidad: modalidad
            }];

            const response = await updateTarifarios(user, tarifarios);

            if (response.success) {
                return {
                    success: true,
                    message: response.message,
                    data: response.data,
                    // Información adicional de la respuesta
                    totalActualizados: response.data?.total_actualizados || 1,
                    totalErrores: response.data?.total_errores || 0,
                    actualizacionesExitosas: response.data?.actualizaciones_exitosas || [],
                    errores: response.data?.errores || null
                };
            } else {
                setUpdateError(response.error);
                return {
                    success: false,
                    error: response.error,
                    details: response.details
                };
            }
        } catch (error) {
            const errorMessage = error.message || 'Error al actualizar tarifario';
            setUpdateError(errorMessage);
            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setUpdating(false);
        }
    }, [user]);

    /**
     * Actualizar múltiples tarifarios
     * @param {Array} tarifarios - Array de objetos { id, precio, modalidad }
     * @returns {Promise<Object>} Resultado de la operación
     */
    const updateBulkTarifarios = useCallback(async (tarifarios) => {
        if (!user) {
            return {
                success: false,
                error: 'Usuario no autenticado'
            };
        }

        if (!Array.isArray(tarifarios) || tarifarios.length === 0) {
            return {
                success: false,
                error: 'Debe proporcionar al menos un tarifario para actualizar'
            };
        }

        setUpdating(true);
        setUpdateError(null);

        try {
            // Validar y procesar cada tarifario
            const tarifariosValidados = tarifarios.map((tarifario, index) => {
                if (!tarifario.id || tarifario.precio === undefined || tarifario.precio === null || !tarifario.modalidad) {
                    throw new Error(`Tarifario en posición ${index + 1}: Todos los campos son requeridos (id, precio, modalidad)`);
                }

                const precioNumerico = parseFloat(tarifario.precio);
                if (isNaN(precioNumerico) || precioNumerico < 0) {
                    throw new Error(`Tarifario en posición ${index + 1}: El precio debe ser un número válido mayor o igual a 0`);
                }

                if (!['Fijo', 'Variable'].includes(tarifario.modalidad)) {
                    throw new Error(`Tarifario en posición ${index + 1}: La modalidad debe ser "Fijo" o "Variable"`);
                }

                return {
                    id: parseInt(tarifario.id),
                    precio: precioNumerico,
                    modalidad: tarifario.modalidad
                };
            });

            const response = await updateTarifarios(user, tarifariosValidados);

            if (response.success) {
                return {
                    success: true,
                    message: response.message,
                    data: response.data,
                    updatedCount: tarifariosValidados.length,
                    // Información adicional de la respuesta
                    totalActualizados: response.data?.total_actualizados || 0,
                    totalErrores: response.data?.total_errores || 0,
                    actualizacionesExitosas: response.data?.actualizaciones_exitosas || [],
                    errores: response.data?.errores || null
                };
            } else {
                setUpdateError(response.error);
                return {
                    success: false,
                    error: response.error,
                    details: response.details
                };
            }
        } catch (error) {
            const errorMessage = error.message || 'Error al actualizar tarifarios';
            setUpdateError(errorMessage);
            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setUpdating(false);
        }
    }, [user]);

    /**
     * Limpiar errores
     */
    const clearUpdateError = useCallback(() => {
        setUpdateError(null);
    }, []);

    return {
        updating,
        updateError,
        updateSingleTarifario,
        updateBulkTarifarios,
        clearUpdateError
    };
};
